/**
 * HOOK: useIndices
 *
 * Adapter hook that wraps the raw getIndices() service and transforms
 * the response to the shape App.jsx expects:
 *   { IPC: { serie: { "YYYYMM": value } }, RIPTE: { serie: { "YYYYMM": value } } }
 *
 * Includes retry logic (3 retries, 1s delay), loading state, and error state.
 *
 * Convivencia: runs alongside the inline indices state in App.jsx.
 * The inline code continues to own setIndicesMovilidad — this hook
 * produces the same data shape for future migration.
 */

import { useState, useEffect } from 'react'
import { getIndices } from '../services/indicesService'

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

export function useIndices() {
  const [indicesMovilidad, setIndicesMovilidad] = useState(null)
  const [indicesLoading, setIndicesLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    let attempts = 0

    const load = async () => {
      setIndicesLoading(true)
      setError(null)

      while (attempts < MAX_RETRIES) {
        attempts += 1
        try {
          const result = await getIndices()
          if (!active) return

          if (result?.success && result?.data && Object.keys(result.data).length > 0) {
            setIndicesMovilidad(result.data)
            setIndicesLoading(false)
            return
          }

          // Empty or invalid data — throw so retry logic kicks in
          throw new Error('Los índices devueltos están vacíos o son inválidos')
        } catch (err) {
          if (!active) return

          if (attempts >= MAX_RETRIES) {
            const msg = err?.message || 'Error cargando índices de movilidad'
            setError(msg)
            setIndicesMovilidad(null)
            setIndicesLoading(false)
            return
          }

          // Wait before retrying
          await sleep(RETRY_DELAY_MS)
        }
      }
    }

    load()
    return () => { active = false }
  }, [])

  return { indicesMovilidad, indicesLoading, error }
}
